// Expose the currentStep
window.currentStep = 1;

// Import field values component
import CreateCorrespondingValueFields from "../Fields/CreateCorrespondingValueFields";

// Import field values
import { SectionClonableFields } from '../Fields/FieldValues';

// Import other utilities.
import { UpdateTotalOnFarmWaterUse } from '../Calculations/Use/TotalOnFarmWaterUse';
import { UpdateWaterSupplyTotal } from '../Calculations/Supply/WaterSupplyTotal';

/**
 * Identify the fields that have been cloned, so we know which values
 * and field selectors we need to initialise via CreateCorrespondingValueFields.
 * 
 * @param   {HTMLElement}    el
 * 
 * @returns {object}
 */
export const identifyFields = el => {
    // Get the section/parent accordion
    const parentID = el.closest('.farm-calc-accordion:not(.farm-calc-accordion--nested)').getAttribute('data-id');
    const field = SectionClonableFields.find(item => item.name === parentID);

    return field ? field.data : null;
};

/**
 * Set the corresponding field values.
 * 
 * @param   {object}    fieldValues
 * @param   {object}    selectors
 * @param   {boolean}   updateSecondaryFieldOptions
 * 
 * @returns {void}
 */
export const setCorrespondingFieldValues = (fieldValues, info) => {
    const Fields = new CreateCorrespondingValueFields({
        data: fieldValues,
        isClone: info.isClone,
        updateSecondaryOptions: info.updateSecondaryOptions,
        updateTertiaryOptions: info.updateTertiaryOptions,
        selectors: {
            primaryField: info.primaryField,
            secondaryField: info.secondaryField,
            tertiaryField: info.tertiaryField
        },
    });

    Fields.init();
};

/**
 * Set the active step, ensure all others remain hidden.
 * 
 * @param   {array}     steps
 * @param   {string}    activeClass
 * 
 * @returns {void}
 */
export const setActiveStep = (steps, activeClass) => {
    const Parent = steps[0].parentNode;

    steps.forEach((step, i) => {
        const Count = i + 1;

        if (Count === window.currentStep) {
            step.classList.add(activeClass);
            step.setAttribute('aria-hidden', false);
        } else {
            step.classList.remove(activeClass);
            step.setAttribute('aria-hidden', true);
        }
    });

    Parent.setAttribute('data-active-step', window.currentStep);
};

/**
 * Get the element from the parent.
 * 
 * @param {object}      parent 
 * @param {object}      selector
 * 
 * @returns {object}
 */
export const getChild = (parent, selector) => parent.querySelector(selector) instanceof HTMLElement ? parent.querySelector(selector) : null;

/**
 * Get the element from the parent.
 * 
 * @param {object}      parent 
 * @param {object}      selector
 * 
 * @returns {object}
 */
export const getChildren = (parent, selector) => Array.from(parent.querySelectorAll(selector)).length > 0 ? Array.from(parent.querySelectorAll(selector)) : [];

/**
 * Get an HTMLElement if it exists.
 * 
 * @param {string}      selector
 * 
 * @returns {HTMLElement|null}
 */
export const getDocumentElement = selector => {
    const element = document.querySelector(selector);
    return element instanceof HTMLElement ? element : null;
};

/**
 * Get an HTMLElement if it exists.
 * 
 * @param {string}      selector
 * 
 * @returns {HTMLElement|null}
 */
export const getDocumentElements = selector => {
    const elements = Array.from(document.querySelectorAll(selector));
    return elements.length > 0 ? elements : [];
};

/**
 * Take in a string and return a hyphenated, sanitised version.
 * 
 * @param {string}      str
 * 
 * @returns {string}
 */
export const sanitiseStringForID = str => {
    return str
        .toLowerCase()
        .replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '')
        .replace(/ /gi, '-')
        .replace(/--/gi, '-')
        ;
};

/**
 * Update the overall total and summary field using the collective sub total
 * fields.
 * 
 * @param   {array}         subTotals 
 * @param   {HTMLElement}   overallTotal 
 * @param   {HTMLElement}   summaryTotal 
 * 
 * @returns {void}
 */
export const updateFieldTotal = (subTotals, overallTotal, summaryField) => {
    let total = 0;

    // field.isConnected (only run calcs on fields that are in the DOM)
    if (subTotals) {
        subTotals
            .filter(field => field.isConnected)
            .forEach(field => {
                total += Number(field.value);
            });
    }
    overallTotal.value = total.toFixed(3);

    if (summaryField) {
        summaryField.value = total.toFixed(3);
    }

    // Call updates on the total accordions
    UpdateTotalOnFarmWaterUse();
    UpdateWaterSupplyTotal();
    
    // Add updated class to totals if necessary
    MarkTotalsUpdated()
};

/**
 * When a sections total accordion has a positive value, mark it updated
 */
function MarkTotalsUpdated() {
  const TotalAccordions = document.querySelectorAll('.farm-calc-accordion[data-total="true"]');

  TotalAccordions.forEach(acc => {
    const inputs = acc.querySelectorAll('input');

    setTimeout(() => {
      let hasValidInput = false;

      inputs.forEach(input => {
        if (Number(input.value) > 0) {
          hasValidInput = true;
        }
      });

      acc.classList.toggle('updated', hasValidInput);
    }, 100);
  });
}
